/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Bryan Sheranian <bryan.sheranian@deliciousai.com>
 */

import { useMutation, useQuery } from '@apollo/client'
import { Nullable, useUrlSearchParams } from '@dai/common'
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Send as SendIcon,
  WarningRounded as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import useT from 'i18n/useT'
import { useState } from 'react'

import CenteredBox from '@/components/CenteredBox'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_TENANT_STORES,
  GET_TENANT_USERS,
  GetTenantStoresQuery,
  GetTenantStoresQueryVariables,
  GetTenantUsersQuery,
  GetTenantUsersQueryVariables,
  Ordering,
  StoreFragment,
  UPDATE_STORE,
  UpdateStoreMutation,
  UpdateStoreMutationVariables,
} from '@/graphql'

type ViewMode = 'stores-to-users' | 'users-to-stores'

const LIMIT = 400

type User = GetTenantUsersQuery['tenantUsers']['results'][0]

const StoreAssignments = () => {
  const { t } = useT()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const {
    state,
    handle: { handleUpdateSearchParams, handleNavTo },
  } = useUrlSearchParams()
  const initialViewMode =
    (state.viewMode as ViewMode | undefined) || 'stores-to-users'
  const initialSelectedItemId =
    (state.selectedItemId as string | undefined) || null

  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const [selectedItemId, setSelectedItemId] = useState<Nullable<string>>(
    initialSelectedItemId,
  )
  const [itemSearchQuery, setItemSearchQuery] = useState('')
  const [associatedItemSearchQuery, setAssociatedItemSearchQuery] = useState('')
  const [updatingItemIds, setUpdatingItemIds] = useState<Set<string>>(new Set())
  const [errorUpdatingAssignment, setErrorUpdatingAssignment] =
    useState<Nullable<string>>(null)

  const [updateStore] = useMutation<
    UpdateStoreMutation,
    UpdateStoreMutationVariables
  >(UPDATE_STORE, {
    notifyOnNetworkStatusChange: true,
  })

  const toggleAssignedItem = async ({
    item,
    associatedItem,
    action,
  }: {
    action: 'add' | 'remove'
    item: StoreFragment | User
    associatedItem: User | StoreFragment
  }) => {
    try {
      setErrorUpdatingAssignment(null)
      setUpdatingItemIds(prev => {
        const newSet = new Set(prev)
        newSet.add(associatedItem.id)
        return newSet
      })
      if (item.__typename === 'Store') {
        if (associatedItem.__typename !== 'User') {
          throw new Error('Associated item must be a user')
        }
        await updateStore({
          variables: {
            data: {
              id: item.id,
              users: {
                [action]: [associatedItem.id],
              },
            },
          },
        })
      } else {
        if (associatedItem.__typename !== 'Store') {
          throw new Error('Associated item must be a store')
        }
        await updateStore({
          variables: {
            data: {
              id: associatedItem.id,
              users: {
                [action]: [item.id],
              },
            },
          },
        })
      }
    } catch (error) {
      setErrorUpdatingAssignment(
        error instanceof Error ? error.message : 'An error occurred',
      )
    } finally {
      setUpdatingItemIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(associatedItem.id)
        return newSet
      })
    }
  }

  const getItemTitle = (
    item: StoreFragment | User,
  ): { title: string; subtitle?: string | null } => {
    if (item.__typename === 'Store') {
      return { title: item.location?.name || 'Unknown Store' }
    } else {
      return item.firstName && item.lastName
        ? { title: `${item.firstName} ${item.lastName}`, subtitle: item.email }
        : { title: item.email || 'Unknown User' }
    }
  }

  const isUserAdmin = (user: User): boolean => {
    return user.groups.some(group => group.isOwner)
  }

  const getAssociatedItems = (item: StoreFragment | User): { id: string }[] => {
    if (item.__typename === 'Store') {
      return item.users.filter(({ isActive }) => isActive)
    } else {
      // not using `user.stores` because it's not updated in the cache (the stores cache is updated though)
      return (
        allStoresData?.data?.tenantStores.results.filter(store =>
          store.users
            .filter(({ isActive }) => isActive)
            .some(({ id }) => id === item.id),
        ) || []
      )
    }
  }

  // Get the visible associated items (filtered by current user's permissions)
  const getVisibleAssociatedItems = (
    item: StoreFragment | User,
  ): { id: string }[] => {
    if (item.__typename === 'Store') {
      return item.users.filter(({ isActive }) => isActive)
    } else {
      // For users, only show stores that the current viewing user has access to
      return (
        storesData?.data?.tenantStores.results.filter(store =>
          store.users
            .filter(({ isActive }) => isActive)
            .some(({ id }) => id === item.id),
        ) || []
      )
    }
  }

  const storeSearchQuery = (
    viewMode === 'stores-to-users' ? itemSearchQuery : associatedItemSearchQuery
  ).trim()
  const storesData = useQuery<
    GetTenantStoresQuery,
    GetTenantStoresQueryVariables
  >(GET_TENANT_STORES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: { limit: LIMIT },
      filters: {
        location_Name: {
          iContains: storeSearchQuery,
        },
        isActive: true,
        onlyMyStores: true,
      },
      order: {
        name: Ordering.ASC,
      },
    },
  })

  const allStoresData = useQuery<
    GetTenantStoresQuery,
    GetTenantStoresQueryVariables
  >(GET_TENANT_STORES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: { limit: LIMIT },
      filters: {
        isActive: true,
      },
    },
  })

  const userSearchQuery = (
    viewMode === 'users-to-stores' ? itemSearchQuery : associatedItemSearchQuery
  ).trim()

  const usersData = useQuery<GetTenantUsersQuery, GetTenantUsersQueryVariables>(
    GET_TENANT_USERS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        pagination: { limit: LIMIT },
        order: {
          firstName: Ordering.ASC,
          lastName: Ordering.ASC,
          email: Ordering.ASC,
        },
        filters: {
          isSuperuser: {
            exact: false,
          },
          AND: {
            isActive: {
              exact: true,
            },
            AND: {
              email: {
                iContains: userSearchQuery,
              },
              OR: {
                firstName: {
                  iStartsWith: userSearchQuery,
                },
                OR: {
                  lastName: {
                    iStartsWith: userSearchQuery,
                  },
                },
              },
            },
          },
        },
      },
    },
  )

  const selectedItem =
    viewMode === 'stores-to-users'
      ? storesData?.data?.tenantStores.results.find(
          ({ id }) => id === selectedItemId,
        )
      : usersData?.data?.tenantUsers.results.find(
          ({ id }) => id === selectedItemId,
        )

  const { title: selectedTitle, subtitle: selectedSubtitle } = selectedItem
    ? getItemTitle(selectedItem)
    : { title: '', subtitle: '' }

  const itemsLoading =
    viewMode === 'stores-to-users'
      ? storesData.loading
      : usersData.loading || allStoresData.loading
  const itemsError =
    viewMode === 'stores-to-users'
      ? storesData.error
      : usersData.error || allStoresData.error
  const items =
    viewMode === 'stores-to-users'
      ? storesData?.data?.tenantStores.results || []
      : usersData?.data?.tenantUsers.results || []

  const itemsTotalCount =
    viewMode === 'stores-to-users'
      ? storesData?.data?.tenantStores.totalCount || 0
      : usersData?.data?.tenantUsers.totalCount || 0

  const associatedItemsLoading =
    viewMode === 'stores-to-users'
      ? usersData.loading || allStoresData.loading
      : storesData.loading
  const associatedItemsError =
    viewMode === 'stores-to-users'
      ? usersData.error || allStoresData.error
      : storesData.error
  const associatedItems =
    viewMode === 'stores-to-users'
      ? usersData?.data?.tenantUsers.results || []
      : storesData?.data?.tenantStores.results || []

  const associatedItemsTotalCount =
    viewMode === 'stores-to-users'
      ? usersData?.data?.tenantUsers.totalCount || 0
      : storesData?.data?.tenantStores.totalCount || 0

  const SPACING = 4

  const handleRefresh = () => {
    storesData.refetch()
    usersData.refetch()
    allStoresData.refetch()
  }
  useRefresh(handleRefresh)

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: SPACING,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING,
        }}
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newValue) => {
            setViewMode(newValue)
            setSelectedItemId(null)
            handleUpdateSearchParams({
              selectedItemId: undefined,
              viewMode: newValue,
            })
          }}
          color="primary"
        >
          <ToggleButton value="stores-to-users">
            {t('storeAssignments.storesToUsers')}
          </ToggleButton>
          <ToggleButton value="users-to-stores">
            {t('storeAssignments.usersToStores')}
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          sx={{ flex: 1, height: 45, maxWidth: 200 }}
          onClick={() => {
            handleNavTo('/admin/users', { replaceParams: { tab: 'invites' } })
          }}
        >
          {t('storeAssignments.inviteUsers')}
        </Button>
      </Box>
      {errorUpdatingAssignment && (
        <Alert
          severity="error"
          onClose={() => setErrorUpdatingAssignment(null)}
        >
          {errorUpdatingAssignment}
        </Alert>
      )}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: SPACING,
          minHeight: 0,
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: isMobile ? '0 0 auto' : 2,
            height: isMobile ? '40vh' : 'auto',
            p: SPACING,
            gap: SPACING,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: SPACING,
            }}
          >
            <Typography variant="h3">
              {viewMode === 'stores-to-users'
                ? t('storeAssignments.storesTableTitle')
                : t('storeAssignments.usersTableTitle')}
            </Typography>
            <TextField
              type="text"
              value={itemSearchQuery}
              fullWidth
              placeholder={t('common.search')}
              onChange={e => setItemSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: itemSearchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setItemSearchQuery('')}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}
          >
            {itemsLoading ? (
              <CenteredBox p={SPACING}>
                <CircularProgress />
              </CenteredBox>
            ) : itemsError ? (
              <CenteredBox p={SPACING}>
                <Alert severity="error">{itemsError.message}</Alert>
              </CenteredBox>
            ) : !itemsTotalCount ? (
              <CenteredBox p={SPACING}>
                <Alert severity="info">
                  {itemSearchQuery
                    ? viewMode === 'stores-to-users'
                      ? t('storeAssignments.noStoresMatchFilter')
                      : t('storeAssignments.noUsersMatchFilter')
                    : viewMode === 'stores-to-users'
                      ? t('storeAssignments.noStores')
                      : t('storeAssignments.noUsers')}
                </Alert>
              </CenteredBox>
            ) : (
              items.map(item => {
                const associatedItems = getAssociatedItems(item)
                const visibleAssociatedItems = getVisibleAssociatedItems(item)
                const isAdmin = item.__typename === 'User' && isUserAdmin(item)
                const { title } = getItemTitle(item)
                return (
                  <ListItemButton
                    key={item.id}
                    onClick={
                      isAdmin
                        ? undefined
                        : () => {
                            setSelectedItemId(item.id)
                            handleUpdateSearchParams({
                              selectedItemId: item.id,
                            })
                          }
                    }
                    selected={selectedItemId === item.id}
                    sx={{
                      flex: 0,
                      gap: 1,
                      cursor: isAdmin ? 'default' : 'pointer',
                      opacity: isAdmin ? 0.6 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        gap: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {title}
                          </Typography>
                          {item.__typename === 'User' && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.email}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {isAdmin ? (
                            <Tooltip
                              title={t('storeAssignments.adminUserNote')}
                            >
                              <InfoIcon
                                color="action"
                                sx={{ fontSize: 20, mr: 1.5 }}
                              />
                            </Tooltip>
                          ) : associatedItems.length ? (
                            associatedItems.length !==
                              visibleAssociatedItems.length &&
                            viewMode === 'users-to-stores' ? (
                              <Tooltip
                                title={t(
                                  'storeAssignments.userHasStoresNotVisible',
                                )}
                              >
                                <Chip label={associatedItems.length} />
                              </Tooltip>
                            ) : (
                              <Chip label={associatedItems.length} />
                            )
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 30,
                                width: 30,
                              }}
                            >
                              <WarningIcon color={'warning'} />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </ListItemButton>
                )
              })
            )}
          </Paper>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: isMobile ? '0 0 auto' : 3,
            height: isMobile ? '40vh' : 'auto',
            p: SPACING,
            gap: SPACING,
          }}
        >
          {selectedItem ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedTitle}
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedSubtitle}
                  </Typography>
                  <Typography variant="caption">
                    {viewMode === 'stores-to-users'
                      ? t('storeAssignments.usersCaption')
                      : t('storeAssignments.storesCaption')}
                  </Typography>
                </Box>
                <TextField
                  type="text"
                  value={associatedItemSearchQuery}
                  fullWidth
                  placeholder={t('common.search')}
                  onChange={e => setAssociatedItemSearchQuery(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: associatedItemSearchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setAssociatedItemSearchQuery('')}
                            size="small"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                }}
              >
                {associatedItemsLoading ? (
                  <CenteredBox p={SPACING}>
                    <CircularProgress />
                  </CenteredBox>
                ) : associatedItemsError ? (
                  <CenteredBox p={SPACING}>
                    <Alert severity="error">
                      {associatedItemsError.message}
                    </Alert>
                  </CenteredBox>
                ) : !associatedItemsTotalCount ? (
                  <CenteredBox p={SPACING}>
                    <Alert severity="info">
                      {associatedItemSearchQuery
                        ? viewMode === 'stores-to-users'
                          ? t('storeAssignments.noUsersMatchFilter')
                          : t('storeAssignments.noStoresMatchFilter')
                        : viewMode === 'stores-to-users'
                          ? t('storeAssignments.noUsers')
                          : t('storeAssignments.noStores')}
                    </Alert>
                  </CenteredBox>
                ) : (
                  associatedItems.map(item => {
                    const isAdmin =
                      item.__typename === 'User' && isUserAdmin(item)
                    const { title } = getItemTitle(item)
                    return (
                      <ListItemButton
                        key={item.id}
                        sx={{
                          display: 'flex',
                          minHeight: isAdmin ? 90 : 65,
                          gap: 1,
                          flexDirection: 'row',
                          flex: 0,
                          cursor: isAdmin ? 'default' : 'pointer',
                          opacity: isAdmin ? 0.6 : 1,
                        }}
                        onClick={
                          isAdmin
                            ? undefined
                            : () => {
                                const checked = getAssociatedItems(
                                  selectedItem,
                                ).some(({ id }) => id === item.id)
                                toggleAssignedItem({
                                  item: selectedItem,
                                  associatedItem: item,
                                  action: checked ? 'remove' : 'add',
                                })
                              }
                        }
                      >
                        {updatingItemIds.has(item.id) ? (
                          <Box
                            sx={{
                              height: 40,
                              width: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        ) : (
                          <Checkbox
                            checked={
                              isAdmin ||
                              getAssociatedItems(selectedItem).some(
                                ({ id }) => id === item.id,
                              )
                            }
                            disabled={isAdmin}
                          />
                        )}
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {title}
                            </Typography>
                            {item.__typename === 'User' && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.email}
                              </Typography>
                            )}
                          </Box>
                          {isAdmin && (
                            <Tooltip
                              title={t('storeAssignments.adminUserNote')}
                            >
                              <InfoIcon
                                color="action"
                                sx={{ fontSize: 20, ml: 1 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </ListItemButton>
                    )
                  })
                )}
              </Paper>
            </>
          ) : (
            <CenteredBox p={SPACING}>
              <Alert severity="info">
                {viewMode === 'stores-to-users'
                  ? t('storeAssignments.selectAStore')
                  : t('storeAssignments.selectAUser')}
              </Alert>
            </CenteredBox>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default StoreAssignments
